// filepath: apps/web/src/features/lists/tree/TreeView.tsx
//
// FR-UI-VIEW-2 AC-1..AC-8 — top-level Tree renderer.
//
// Mirrors KanbanBoard.tsx's 6-state envelope (FR-UI-CORE-10 +
// FR-UI-VIEW-2 AC-5). TreeView is read-only at this stage (no
// drag-drop / reparent surface), so the move-related validation
// state in KanbanBoard (the `validationRows` branch) has no
// equivalent here. The error-state envelope is otherwise
// identical: loading / fetch-failure / permission-denied /
// stale-version / rate-limit / ready.
//
// BRANCHING:
//   EntityListPage selects <TreeView> when
//   `definition.definition_json.view_config.default_view_type ===
//   "tree"`. TreeView resolves the `tree.parent_field` and the
//   `tree.depth_cap` from the same view_config block. The
//   component is intentionally kept "stupid" — all I/O lives in
//   `useTreeRecords`, all expansion state lives in
//   `useTreeExpansion`, all tree topology lives in
//   `buildTreeIndex`. The renderer is responsible only for
//   laying out the rows.
//
// SECURITY / INVARIANT REMINDERS:
//   - INV-1 / INV-2 / INV-7: enforced inside useTreeRecords and
//     normaliseTreeRecord.
//   - INV-9: text nodes only (no dangerouslySetInnerHTML anywhere
//     in this directory).
//   - ISS-303: every interactive element carries data-testid.
//   - ISS-306: token-only widths (no hard-coded px numbers in the
//     chrome).

import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import { Banner } from "@/design-system";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import type { EntityDefinitionWire, EntityListStatus } from "../types";
import type {
  EntityTreeConfigWire,
  EntityViewConfigWire,
  TreeRecord,
} from "./types";
import { TreeNode } from "./TreeNode";
import { TreeCountBanner, shouldShowTreeCountBanner } from "./TreeCountBanner";
import { useTreeRecords, TREE_RECORDS_CAP } from "./hooks/useTreeRecords";
import { useTreeExpansion } from "./hooks/useTreeExpansion";
import { buildTreeIndex } from "./hooks/buildTreeIndex";

export interface TreeViewProps {
  /** Target entity type id. */
  entityType: string;
  /**
   * Resolved entity definition. Carries the `view_config` block
   * (defaults + tree parameters). Optional — when omitted, the
   * tree view renders its own skeleton until the parent provides
   * one. The EntityListPage dispatch site supplies this.
   */
  definition?: EntityDefinitionWire | null;
}

/**
 * Classify a TanStack Query result into one of the 5 mandatory
 * error states (FR-UI-CORE-10 + FR-UI-VIEW-2 AC-5). Mirrors
 * `classifyKanbanError` exactly — the Tree view inherits the same
 * status taxonomy so the user's chrome transitions are identical.
 */
function classifyTreeError(err: ApiError): EntityListStatus {
  if (err.status === 401 || err.status === 403) return "permission-denied";
  if (err.status === 429) return "rate-limit";
  if (err.status === 410) return "stale-version";
  if (err.status === 404) return "permission-denied";
  return "fetch-failure";
}

/**
 * Resolve the `view_config` block from a definition. Mirrors
 * `resolveViewConfig` in KanbanBoard; kept local to this module
 * because the wire shape narrowed here (the `tree?:` field is
 * not part of the kanban module's wire alias).
 */
function resolveViewConfig(
  definition: EntityDefinitionWire | null | undefined,
): EntityViewConfigWire | null {
  if (!definition) return null;
  const defJson = definition.definition_json;
  if (!defJson) return null;
  const raw = (defJson as unknown as { view_config?: unknown }).view_config;
  if (!raw || typeof raw !== "object") return null;
  return raw as EntityViewConfigWire;
}

/**
 * Format the title for a record. The renderer treats the FIRST
 * field key on the wire response as the title source — INV-2
 * (server-stripped fields only). If the server returns no fields,
 * fall back to the placeholder `<record title>`.
 */
function formatTitle(record: TreeRecord): string {
  const keys = Object.keys(record.fields);
  if (keys.length === 0) return "<record title>";
  const firstKey = keys[0];
  if (!firstKey) return "<record title>";
  const v = record.fields[firstKey];
  if (v === null || v === undefined || v === "") return "<record title>";
  return String(v);
}

/**
 * TreeView — top-level renderer.
 */
export function TreeView({
  entityType,
  definition,
}: TreeViewProps): React.ReactElement {
  const { t } = useTranslation();

  const viewConfig = resolveViewConfig(definition);
  const treeConfig: EntityTreeConfigWire | null = viewConfig?.tree ?? null;
  const parentField = treeConfig?.parent_field ?? "";
  const viewConfigVersion = definition?.version ?? 0;

  // Expansion state — owned by the renderer (read-only surface,
  // no URL round-trip needed).
  const expansion = useTreeExpansion();

  const recordsQuery = useTreeRecords({
    entityType,
    viewConfigVersion,
    parentField,
    enabled: parentField.length > 0 && entityType.length > 0,
  });

  // Compute the tree topology (pure) and seed expansion once per
  // root-set change. We seed by writing the root ids into the
  // expansion set — this is the "roots start expanded" behaviour
  // from AC-2 / AC-3.
  //
  // Seed strategy: we track the last-applied root-id signature in
  // a ref. When the signature changes (new dataset, new root set,
  // re-publication), we call `expansion.setAll(rootIds)`. The
  // ref is NOT used for rendering — it just gates the seeding
  // pass.
  //
  // Subtle ordering: the records-query result lands AFTER the
  // first render. The layout effect below fires synchronously
  // after that render, calling setAll before paint. The user
  // sees the tree with roots already expanded on the first
  // paint. Subsequent toggles mutate the live expansion set
  // directly and the seed is not re-applied.
  const records: ReadonlyArray<TreeRecord> = recordsQuery.data?.records ?? [];
  const total = recordsQuery.data?.total ?? null;

  const index = useMemo(() => buildTreeIndex(records), [records]);
  const lastSeedRef = useRef<string>("");

  useLayoutEffect(() => {
    // Build a stable signature from the sorted root ids so a
    // re-ordering of the records array (which buildTreeIndex
    // preserves) does not trigger a re-seed.
    const signature = index.roots
      .map((r) => r.id)
      .slice()
      .sort()
      .join("|");
    if (signature === lastSeedRef.current) return;
    lastSeedRef.current = signature;
    expansion.setAll(index.roots.map((r) => r.id));
  }, [index.roots, expansion]);

  const toggleNode = useCallback(
    (id: string) => {
      expansion.toggle(id);
    },
    [expansion],
  );

  const renderChild = useCallback(
    (child: TreeRecord, depth: number): React.ReactNode => {
      const childChildren = index.childrenById.get(child.id) ?? [];
      return (
        <TreeNode
          key={child.id}
          record={child}
          depth={depth}
          isExpanded={expansion.expanded.has(child.id)}
          children={childChildren}
          isOrphan={index.orphans.some((o) => o.id === child.id)}
          isLeaf={childChildren.length === 0}
          onToggle={toggleNode}
          renderChild={renderChild}
          titleLabel={formatTitle(child)}
        />
      );
    },
    [expansion.expanded, index.childrenById, index.orphans, toggleNode],
  );

  // Loading — the records query is in flight on the first render.
  if (
    recordsQuery.isLoading ||
    (recordsQuery.isFetching && !recordsQuery.data)
  ) {
    return (
      <SkeletonLayout
        data-testid="tree-view--loading"
        aria-label={t("lists.tree.title", "<entity type>")}
      />
    );
  }

  // Error states — mirrors the KanbanBoard envelope.
  if (recordsQuery.error) {
    const status = classifyTreeError(recordsQuery.error);
    if (status === "permission-denied") {
      return (
        <section
          data-testid="tree-view--permission-denied"
          aria-label={t("lists.tree.title", "<entity type>")}
        >
          <PermissionDenied />
        </section>
      );
    }
    if (status === "stale-version") {
      return (
        <section
          data-testid="tree-view--stale-version"
          aria-label={t("lists.tree.title", "<entity type>")}
        >
          <StaleVersionError />
        </section>
      );
    }
    if (status === "rate-limit") {
      return (
        <section
          data-testid="tree-view--rate-limit"
          aria-label={t("lists.tree.title", "<entity type>")}
        >
          <RateLimitBackpressure
            retryAfterSeconds={recordsQuery.error.retryAfterSeconds}
          />
        </section>
      );
    }
    // fetch-failure (default)
    return (
      <section
        data-testid="tree-view--fetch-failure"
        aria-label={t("lists.tree.title", "<entity type>")}
      >
        <FetchError
          message={t(
            "lists.tree.error.fetch_failure",
            "Could not load records.",
          )}
          onRetry={() => {
            void recordsQuery.refetch();
          }}
        />
      </section>
    );
  }

  // Empty state — show a banner so the user gets feedback that
  // the view is functional, just empty.
  if (index.roots.length === 0) {
    return (
      <section
        data-testid="tree-view"
        aria-label={t("lists.tree.title", "<entity type>")}
      >
        <TreeCountBanner shown={0} total={total} cap={TREE_RECORDS_CAP} />
        <Banner
          tone="info"
          data-testid="tree-view--empty"
          style={{
            marginBlockStart: "var(--space-4)",
          }}
        >
          {t("lists.tree.error.empty", "<entity type> has no records.")}
        </Banner>
      </section>
    );
  }

  return (
    <section
      aria-label={t("lists.tree.title", "<entity type>")}
      data-testid="tree-view"
    >
      {shouldShowTreeCountBanner(records.length, total, TREE_RECORDS_CAP) ? (
        <TreeCountBanner
          shown={records.length}
          total={total}
          cap={TREE_RECORDS_CAP}
        />
      ) : null}
      <ul
        data-testid="tree-view-canvas"
        style={{
          listStyle: "none",
          margin: 0,
          paddingInlineStart: 0,
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-1)",
        }}
      >
        {index.roots.map((root) => renderChild(root, 0))}
      </ul>
    </section>
  );
}
